import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  advancement as advApi,
  ApiError,
  centsToUSD,
  finance as financeApi,
  type Payment,
  type ScoutDues,
  type ScoutSummary,
} from '../api';
import { useUnits } from '../units';
import { Advancement } from './ScoutDetail';

export function Family() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole } = useUnits();
  // Guardians hold the PARENT role in the unit; admins/leaders use the Roster instead.
  const mayView = hasRole('PARENT');

  const [scouts, setScouts] = useState<ScoutSummary[] | null>(null);
  const [dues, setDues] = useState<Record<string, ScoutDues>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mayView) return;
    setError(null);
    advApi
      .mine(unitId)
      .then(setScouts)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
    financeApi
      .dues(unitId)
      .then((rows) => setDues(Object.fromEntries(rows.map((d) => [d.scoutId, d]))))
      .catch(() => setDues({}));
  }, [mayView, unitId]);

  if (!mayView) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>My Scout{scouts && scouts.length !== 1 ? 's' : ''}</h1>
      <p className="muted">{currentUnit?.name} · advancement progress (read-only)</p>
      {error && <p className="error">{error}</p>}
      {!scouts && !error && <p className="muted">Loading…</p>}
      {scouts?.length === 0 && (
        <p className="muted">
          No Scouts are linked to your account in this unit. Ask a den leader or pack admin to link
          you as a guardian.
        </p>
      )}
      {scouts?.map((s) => (
        <section key={s.scoutId} style={{ marginTop: 24 }}>
          <h2 style={{ marginBottom: 4 }}>
            {s.firstName} {s.lastName}
          </h2>
          <p className="muted small">
            {s.rankLabel ? `${s.rankLabel} · ` : ''}
            {s.readyToAward
              ? 'ready to award'
              : `${s.requiredComplete}/${s.requiredTotal} required · ${s.electivesComplete}/${s.minElectives} electives`}
          </p>
          {dues[s.scoutId] && dues[s.scoutId]!.lines.length > 0 && (
            <div className="callout" style={{ marginBottom: 12 }}>
              <strong>
                Balance: {centsToUSD(dues[s.scoutId]!.balanceCents)}
                <span className="muted small">
                  {' '}
                  ({centsToUSD(dues[s.scoutId]!.totalCents)} charged, {centsToUSD(dues[s.scoutId]!.paidCents)}{' '}
                  paid
                  {dues[s.scoutId]!.creditsCents > 0 &&
                    `, ${centsToUSD(dues[s.scoutId]!.creditsCents)} fundraiser credit`}
                  )
                </span>
              </strong>
              <ul className="reqs" style={{ marginTop: 4 }}>
                {dues[s.scoutId]!.lines.map((l, i) => (
                  <li key={i}>
                    {l.name}
                    {l.note ? ` (${l.note})` : ''} — {centsToUSD(l.owedCents)}
                  </li>
                ))}
              </ul>
              <PaymentHistory unitId={unitId} scoutId={s.scoutId} />
            </div>
          )}
          <Advancement unitId={unitId} scoutId={s.scoutId} canEdit={false} />
        </section>
      ))}
    </div>
  );
}

function PaymentHistory({ unitId, scoutId }: { unitId: string; scoutId: string }) {
  const [rows, setRows] = useState<Payment[]>([]);
  const load = useCallback(() => {
    financeApi.payments.list(unitId, scoutId).then(setRows).catch(() => setRows([]));
  }, [unitId, scoutId]);
  useEffect(load, [load]);

  if (rows.length === 0) return null;
  return (
    <table className="grid" style={{ marginTop: 8 }}>
      <thead>
        <tr>
          <th>Date</th>
          <th>Amount</th>
          <th>Method</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} className={p.reversed ? 'muted' : ''}>
            <td>{p.paidAt}</td>
            <td>{centsToUSD(p.amountCents)}</td>
            <td>
              {p.method}
              {p.reversed && ' · reversed'}
            </td>
            <td className="row-actions">
              {!p.reversed && (
                <Link to={`/units/${unitId}/finance/receipt/${p.id}`} target="_blank">
                  Receipt
                </Link>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
